/* LegadoDiffModal — Modal de revisão dedicada para sementes "legada_enriquecida".
 * Exibe lado a lado: DADOS LEGADOS (planilha) × ENRIQUECIMENTO IA.
 * Ações: Aprovar / Rejeitar / Arquivar / Editar Completo (abre SeedDetailModal).
 */
import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { AREAS, CLASSIFICACOES, PESTEL, RECORTES, labelOf } from "@/lib/constants";
import { X, FileText, Database, Brain, Check, XCircle, Archive, Edit3, ExternalLink, AlertTriangle, Sparkles } from "lucide-react";
import SeedDetailModal from "@/components/SeedDetailModal";

const fmt = (v) => (v == null || v === "" ? "—" : v);
const fmtArr = (v) => (Array.isArray(v) && v.length ? v.join(", ") : "—");
const fmtScore = (v) => (typeof v === "number" && v > 0 ? `${v}` : "—");

const Badges = ({ items, options, className }) => {
  if (!items || items.length === 0) return <span className="text-[var(--text-muted)] text-xs">—</span>;
  const list = Array.isArray(items) ? items : [items];
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((v, i) => (
        <span key={`${v}-${i}`} className={`badge text-[10px] ${className || "badge-gold"}`}>
          {options ? labelOf(options, v) : v}
        </span>
      ))}
    </div>
  );
};

const Row = ({ label, legacy, ai, highlight }) => (
  <div className="grid grid-cols-[120px,1fr,1fr] gap-3 py-2 border-b border-[var(--border)]/40 hover:bg-[var(--surface-2)]/40 transition-colors">
    <div className="label-mil text-[10px] self-start pt-1">{label}</div>
    <div className={`text-xs ${highlight === "legacy" ? "font-semibold text-[var(--info)]" : ""}`}>
      {typeof legacy === "string" || typeof legacy === "number" ? <span>{fmt(legacy)}</span> : legacy || <span className="text-[var(--text-muted)]">—</span>}
    </div>
    <div className={`text-xs ${highlight === "ai" ? "font-semibold text-[var(--gold)]" : ""}`}>
      {typeof ai === "string" || typeof ai === "number" ? <span>{fmt(ai)}</span> : ai || <span className="text-[var(--text-muted)]">—</span>}
    </div>
  </div>
);

export default function LegadoDiffModal({ seedId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [openFullEdit, setOpenFullEdit] = useState(null);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showArchive, setShowArchive] = useState(false);
  const [archiveNote, setArchiveNote] = useState("");
  const [showQuickEdit, setShowQuickEdit] = useState(false);
  const [edits, setEdits] = useState({});

  useEffect(() => {
    if (!seedId) return;
    setData(null); setErr("");
    setShowReject(false); setRejectReason("");
    setShowArchive(false); setArchiveNote("");
    setShowQuickEdit(false); setEdits({});
    api.get(`/seeds/${seedId}`)
      .then((r) => {
        setData(r.data);
        // pre-popula edits com valores atuais do seed
        const s = r.data?.seed || {};
        setEdits({
          classificacao_semente: s.classificacao_semente || "",
          horizonte_temporal: s.horizonte_temporal || "",
          area_tematica_principal: s.area_tematica_principal || "",
          recorte_geografico: s.recorte_geografico || "",
          perspectiva_pestel: Array.isArray(s.perspectiva_pestel) ? s.perspectiva_pestel : [],
          observacoes: "",
        });
      })
      .catch((e) => setErr(formatErr(e)));
  }, [seedId]);

  if (!seedId) return null;

  const seed = data?.seed || {};
  const src = data?.source || {};

  const doAction = async (action, payload = {}) => {
    setBusy(true); setErr("");
    try {
      const body = { action, ...payload };
      await api.post(`/seeds/${seedId}/decision`, body);
      onChanged?.();
      onClose();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  // Aprova após aplicar pequenos ajustes (PUT + decision em sequência)
  const doApproveWithEdit = async () => {
    setBusy(true); setErr("");
    try {
      const diff = {};
      const s = seed || {};
      if (edits.classificacao_semente && edits.classificacao_semente !== s.classificacao_semente)
        diff.classificacao_semente = edits.classificacao_semente;
      if (edits.horizonte_temporal && edits.horizonte_temporal !== s.horizonte_temporal)
        diff.horizonte_temporal = edits.horizonte_temporal;
      if (edits.area_tematica_principal && edits.area_tematica_principal !== s.area_tematica_principal)
        diff.area_tematica_principal = edits.area_tematica_principal;
      if (edits.recorte_geografico && edits.recorte_geografico !== s.recorte_geografico)
        diff.recorte_geografico = edits.recorte_geografico;
      const pestelArr = edits.perspectiva_pestel || [];
      const curArr = s.perspectiva_pestel || [];
      if (JSON.stringify([...pestelArr].sort()) !== JSON.stringify([...curArr].sort()))
        diff.perspectiva_pestel = pestelArr;
      if (edits.observacoes && edits.observacoes.trim())
        diff.observacoes = `[Revisor] ${edits.observacoes.trim()}${s.observacoes ? "\n\n— Anterior —\n" + s.observacoes : ""}`;
      // 1) PUT atualizando só o que mudou
      if (Object.keys(diff).length > 0) {
        await api.put(`/seeds/${seedId}`, diff);
      }
      // 2) Aprovar
      await api.post(`/seeds/${seedId}/decision`, { action: "aprovar" });
      onChanged?.();
      onClose();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const togglePestel = (v) => {
    setEdits((prev) => {
      const arr = prev.perspectiva_pestel || [];
      return { ...prev, perspectiva_pestel: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
           onClick={onClose} data-testid="legado-diff-overlay">
        <div className="card-mil w-full max-w-7xl my-8 max-h-[92vh] flex flex-col gold-accent-top"
             onClick={(e) => e.stopPropagation()} data-testid="legado-diff-modal">
          {/* Header */}
          <div className="p-5 border-b border-[var(--border)] flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Database size={12} className="text-[var(--info)]" />
                <span className="label-mil text-[var(--gold)]">Revisão Legada · DIFF Humano × IA</span>
                {src.legacy_id != null && (
                  <span className="badge badge-muted text-[10px]">ID LEGADO #{src.legacy_id}</span>
                )}
                <span className="badge badge-gold text-[10px]">Score IA: {fmtScore(seed.confianca_ia ?? seed.ai_confidence_score)}</span>
              </div>
              <h2 className="font-display font-bold text-xl text-[var(--text)] leading-tight truncate" title={src.titulo}>
                {src.titulo || seed.titulo_insumo}
              </h2>
              <p className="text-[11px] text-[var(--text-muted)] mt-1">
                Compare os dados originais da Base Legada LSC com o enriquecimento sugerido pelo Gemini antes de aprovar.
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[var(--surface-2)] rounded text-[var(--text-muted)] hover:text-[var(--text)]"
                    data-testid="legado-diff-close">
              <X size={18} />
            </button>
          </div>

          {/* Column headers */}
          <div className="px-5 pt-4 grid grid-cols-[120px,1fr,1fr] gap-3 sticky top-0 bg-[var(--surface)] z-10 border-b border-[var(--border)]">
            <div></div>
            <div className="pb-2 flex items-center gap-2">
              <Database size={14} className="text-[var(--info)]" />
              <span className="font-display font-bold text-sm">Base Legada (humano)</span>
              <span className="badge badge-muted text-[9px]">PLANILHA ORIGINAL</span>
            </div>
            <div className="pb-2 flex items-center gap-2">
              <Brain size={14} className="text-[var(--gold)]" />
              <span className="font-display font-bold text-sm">Enriquecimento IA</span>
              <span className="badge badge-gold text-[9px]">SUGESTÃO GEMINI · NÃO APROVADO</span>
            </div>
          </div>

          {/* Body — Diff Rows */}
          <div className="flex-1 overflow-auto scrollbar-thin px-5 pb-5" data-testid="legado-diff-body">
            {err && (
              <div className="my-3 text-sm text-[var(--danger)] border border-[var(--danger)]/40 bg-[var(--danger)]/10 p-3 rounded">
                <AlertTriangle size={14} className="inline mr-1" />{err}
              </div>
            )}
            {!data && !err && <div className="py-10 text-center text-[var(--text-muted)]">Carregando…</div>}

            {data && (
              <>
                {/* Identificação */}
                <Row label="Título" legacy={src.titulo} ai={seed.titulo_insumo} highlight="ai" />
                <Row label="Autores"
                     legacy={fmtArr(src.autores)}
                     ai={Array.isArray(seed.autores) && seed.autores.length ? fmtArr(seed.autores) : fmtArr(src.autores)} />
                <Row label="Ano publicação" legacy={src.ano_publicacao} ai={<span className="text-[var(--text-muted)] italic">ver horizonte temporal abaixo</span>} />
                <Row label="Idioma" legacy={src.idioma} ai={<span className="text-[var(--text-muted)] italic">—</span>} />
                <Row label="Tipo" legacy={src.tipo_fonte} ai={<span className="text-[var(--text-muted)] italic">—</span>} />
                <Row label="DOI" legacy={src.doi} ai={<span className="text-[var(--text-muted)] italic">—</span>} />
                <Row label="Link"
                     legacy={src.link_original ? <a href={src.link_original} target="_blank" rel="noreferrer" className="text-[var(--info)] hover:underline break-all"><ExternalLink size={10} className="inline mr-1" />{src.link_original}</a> : "—"}
                     ai={<span className="text-[var(--text-muted)] italic">—</span>} />
                <Row label="Status do link" legacy={<span className="badge badge-muted text-[10px]">{src.status_link || "—"}</span>} ai={<span className="text-[var(--text-muted)] italic">—</span>} />
                <Row label="Importado por" legacy={src.responsavel_legado} ai={<span className="text-[var(--text-muted)] italic">Enriquecido pela IA Gemini</span>} />

                {/* Conteúdo */}
                <div className="mt-4 mb-2 pb-1 border-b border-[var(--border)]">
                  <span className="label-mil text-[var(--gold)] text-[10px]">CONTEÚDO ANALÍTICO</span>
                </div>
                <Row label="Tema / Categoria" legacy={src.tema_legado} ai={labelOf(AREAS, seed.area_tematica_principal)} highlight="ai" />
                <Row label="Resumo"
                     legacy={src.resumo_legado ? <p className="whitespace-pre-wrap leading-snug">{src.resumo_legado}</p> : "—"}
                     ai={seed.resumo_executivo ? <p className="whitespace-pre-wrap leading-snug text-[var(--text)]">{seed.resumo_executivo}</p> : "—"}
                     highlight="ai" />
                <Row label="Observações"
                     legacy={src.observacoes_legado ? <p className="whitespace-pre-wrap leading-snug">{src.observacoes_legado}</p> : "—"}
                     ai={seed.justificativa_classificacao ? <p className="whitespace-pre-wrap leading-snug italic">{seed.justificativa_classificacao}</p> : "—"} />

                {/* Classificação prospectiva (somente IA) */}
                <div className="mt-4 mb-2 pb-1 border-b border-[var(--border)]">
                  <span className="label-mil text-[var(--gold)] text-[10px]">CLASSIFICAÇÃO PROSPECTIVA (sugestão IA)</span>
                </div>
                <Row label="Classificação"
                     legacy={<span className="text-[var(--text-muted)] italic">não classificado na planilha</span>}
                     ai={<Badges items={[seed.classificacao_semente]} options={CLASSIFICACOES} />}
                     highlight="ai" />
                <Row label="Perspectiva PESTEL"
                     legacy={<span className="text-[var(--text-muted)] italic">—</span>}
                     ai={<Badges items={seed.perspectiva_pestel} options={PESTEL} className="badge-info" />}
                     highlight="ai" />
                <Row label="Áreas secundárias"
                     legacy={<span className="text-[var(--text-muted)] italic">—</span>}
                     ai={<Badges items={seed.areas_tematicas_secundarias} options={AREAS} className="badge-muted" />} />
                <Row label="Recorte geográfico"
                     legacy={<span className="text-[var(--text-muted)] italic">—</span>}
                     ai={<Badges items={[seed.recorte_geografico]} options={RECORTES} />} />
                <Row label="País impacto"
                     legacy={<span className="text-[var(--text-muted)] italic">—</span>}
                     ai={<Badges items={seed.pais_impacto} className="badge-info" />} />
                <Row label="Horizonte temporal"
                     legacy={<span className="text-[var(--text-muted)] italic">—</span>}
                     ai={seed.horizonte_temporal}
                     highlight="ai" />
                <Row label="Palavras-chave"
                     legacy={<span className="text-[var(--text-muted)] italic">—</span>}
                     ai={<Badges items={seed.palavras_chave} className="badge-muted" />} />
                <Row label="Recomendação IA"
                     legacy={<span className="text-[var(--text-muted)] italic">—</span>}
                     ai={seed.recomendacao_ia ? <p className="italic text-[var(--gold)]">&ldquo;{seed.recomendacao_ia}&rdquo;</p> : "—"} />
                <Row label="Confiança IA"
                     legacy={<span className="text-[var(--text-muted)] italic">—</span>}
                     ai={<div className="flex items-center gap-2">
                           <span className="font-mono font-bold">{fmtScore(seed.confianca_ia ?? seed.ai_confidence_score)}</span>
                           <span className="text-[10px] text-[var(--text-muted)]">/ 100</span>
                         </div>} />
              </>
            )}
          </div>

          {/* Quick Edit + Aprovar — ajustes rápidos do revisor */}
          {showQuickEdit && (
            <div className="px-5 py-4 border-t border-[var(--gold)]/40 bg-[var(--gold)]/5" data-testid="quick-edit-form">
              <div className="flex items-center justify-between mb-3">
                <div className="label-mil text-[var(--gold)] flex items-center gap-1">
                  <Sparkles size={11} /> Ajuste rápido antes de aprovar
                </div>
                <div className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-widest">
                  altere apenas o necessário · campos vazios são ignorados
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <label className="label-mil text-[10px] mb-1 block">Classificação</label>
                  <select
                    className="input-mil text-xs"
                    value={edits.classificacao_semente || ""}
                    onChange={(e) => setEdits((p) => ({ ...p, classificacao_semente: e.target.value }))}
                    data-testid="qe-classificacao"
                  >
                    <option value="">— manter —</option>
                    {CLASSIFICACOES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mil text-[10px] mb-1 block">Horizonte temporal</label>
                  <input
                    className="input-mil text-xs"
                    placeholder="Ex.: 2030, 2030-2035, 2040+"
                    value={edits.horizonte_temporal || ""}
                    onChange={(e) => setEdits((p) => ({ ...p, horizonte_temporal: e.target.value }))}
                    data-testid="qe-horizonte"
                  />
                </div>
                <div>
                  <label className="label-mil text-[10px] mb-1 block">Área temática principal</label>
                  <select
                    className="input-mil text-xs"
                    value={edits.area_tematica_principal || ""}
                    onChange={(e) => setEdits((p) => ({ ...p, area_tematica_principal: e.target.value }))}
                    data-testid="qe-area"
                  >
                    <option value="">— manter —</option>
                    {AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mil text-[10px] mb-1 block">Recorte geográfico</label>
                  <select
                    className="input-mil text-xs"
                    value={edits.recorte_geografico || ""}
                    onChange={(e) => setEdits((p) => ({ ...p, recorte_geografico: e.target.value }))}
                    data-testid="qe-recorte"
                  >
                    <option value="">— manter —</option>
                    {RECORTES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="label-mil text-[10px] mb-1 block">Perspectiva PESTEL</label>
                  <div className="flex flex-wrap gap-1.5" data-testid="qe-pestel-group">
                    {PESTEL.map((p) => {
                      const active = (edits.perspectiva_pestel || []).includes(p.value);
                      return (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => togglePestel(p.value)}
                          className={`badge text-[10px] cursor-pointer transition ${active ? "badge-info" : "badge-muted opacity-60 hover:opacity-100"}`}
                          data-testid={`qe-pestel-${p.value}`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="md:col-span-3">
                  <label className="label-mil text-[10px] mb-1 block">Observação do revisor (opcional)</label>
                  <textarea
                    rows={2}
                    className="input-mil text-xs"
                    placeholder="Será prefixada com [Revisor] e mantém o conteúdo anterior."
                    value={edits.observacoes || ""}
                    onChange={(e) => setEdits((p) => ({ ...p, observacoes: e.target.value }))}
                    data-testid="qe-observacoes"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button className="btn-ghost text-xs" onClick={() => setShowQuickEdit(false)} data-testid="qe-cancel">Cancelar</button>
                <button className="btn-gold text-xs flex items-center gap-1" disabled={busy}
                        onClick={doApproveWithEdit} data-testid="qe-confirm">
                  <Sparkles size={11} /> Salvar Ajustes e Aprovar
                </button>
              </div>
            </div>
          )}

          {/* Archive form */}
          {showArchive && (
            <div className="px-5 py-3 border-t border-[var(--warn)]/40 bg-[var(--warn)]/5" data-testid="archive-form">
              <div className="label-mil text-[var(--warn)] mb-2 flex items-center gap-1">
                <Archive size={11} /> Observação administrativa para arquivamento
              </div>
              <textarea
                rows={2}
                className="input-mil text-xs"
                placeholder="Ex.: Fonte duplicada. Aguardando reavaliação do coordenador…"
                value={archiveNote}
                onChange={(e) => setArchiveNote(e.target.value)}
                data-testid="archive-note"
              />
              <div className="flex justify-end gap-2 mt-2">
                <button className="btn-ghost text-xs" onClick={() => setShowArchive(false)} data-testid="archive-cancel">Cancelar</button>
                <button className="btn-warn text-xs" disabled={busy || !archiveNote.trim()}
                        onClick={() => doAction("arquivar", { archive_note: archiveNote })} data-testid="archive-confirm">
                  Confirmar Arquivamento
                </button>
              </div>
            </div>
          )}

          {/* Reject form */}
          {showReject && (
            <div className="px-5 py-3 border-t border-[var(--danger)]/40 bg-[var(--danger)]/5" data-testid="reject-form">
              <div className="label-mil text-[var(--danger)] mb-2 flex items-center gap-1">
                <XCircle size={11} /> Motivo da rejeição
              </div>
              <textarea
                rows={2}
                className="input-mil text-xs"
                placeholder="Justifique a recusa do enriquecimento IA…"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                data-testid="reject-reason"
              />
              <div className="flex justify-end gap-2 mt-2">
                <button className="btn-ghost text-xs" onClick={() => setShowReject(false)} data-testid="reject-cancel">Cancelar</button>
                <button className="btn-danger text-xs" disabled={busy || !rejectReason.trim()}
                        onClick={() => doAction("rejeitar", { rejection_reason: rejectReason })} data-testid="reject-confirm">
                  Confirmar Rejeição
                </button>
              </div>
            </div>
          )}

          {/* Footer actions */}
          <div className="p-3 border-t border-[var(--border)] flex flex-wrap justify-between items-center gap-2 bg-[var(--bg)]/40">
            <div className="text-[10px] font-mono text-[var(--text-muted)] flex items-center gap-1">
              <FileText size={10} />
              <span>FONTE: {src.source_id?.slice(0, 18) || "—"} · SEMENTE: {seedId?.slice(0, 18)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => setOpenFullEdit(seedId)}
                      data-testid="action-edit-full" disabled={busy}>
                <Edit3 size={11} /> Editar Completo
              </button>
              <button className="btn-ghost text-xs flex items-center gap-1"
                      onClick={() => { setShowQuickEdit(false); setShowReject(false); setShowArchive(true); }}
                      data-testid="action-arquivar" disabled={busy || showArchive}>
                <Archive size={11} /> Arquivar
              </button>
              <button className="btn-danger text-xs flex items-center gap-1"
                      onClick={() => { setShowQuickEdit(false); setShowArchive(false); setShowReject(true); }}
                      data-testid="action-rejeitar" disabled={busy || showReject}>
                <XCircle size={11} /> Rejeitar
              </button>
              <button className="btn-gold text-xs flex items-center gap-1"
                      onClick={() => { setShowReject(false); setShowArchive(false); setShowQuickEdit(true); }}
                      data-testid="action-aprovar-com-edicao" disabled={busy || showQuickEdit || seed.status_validacao === "aprovado"}>
                <Sparkles size={11} /> Aprovar c/ Edição
              </button>
              <button className="btn-primary text-xs flex items-center gap-1" onClick={() => doAction("aprovar")}
                      data-testid="action-aprovar" disabled={busy || seed.status_validacao === "aprovado"}>
                <Check size={11} /> Aprovar Enriquecimento
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal aninhado de edição completa */}
      <SeedDetailModal seedId={openFullEdit} onClose={() => setOpenFullEdit(null)}
                       onChanged={() => { onChanged?.(); onClose(); }} />
    </>
  );
}
